import {ReactNode} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

interface EmpionDialogProps {
  open: boolean;
  maxWidth?: DialogProps['maxWidth'];
  onClose(e: React.MouseEvent): void;
  children: ReactNode;
  sx?: SxProps<Theme>;
  backgroundColor?: string;
  title?: string | null;
  subTitle?: string | null;
  sxTitle?: SxProps<Theme>;
  sxSubTitle?: SxProps<Theme>;
  showCloseButton?: boolean;
}

export default function EmpionDialog({
  open,
  maxWidth = 'md',
  onClose,
  children,
  sx,
  title,
  backgroundColor,
  sxTitle,
  subTitle,
  sxSubTitle,
  showCloseButton,
}: EmpionDialogProps) {
  const bgColor = backgroundColor ? backgroundColor : 'white';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      data-testid='empion-dialog'
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      BackdropProps={{
        onMouseDown: (e: React.MouseEvent) => {
          // Avoid “click-through” to elements under the dialog (e.g. kanban card → match details).
          e.preventDefault();
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '736px',
          padding: '24px',
          borderRadius: '16px',
          backgroundColor: bgColor,
          backgroundImage: 'none',
          position: 'relative',
        },
        '& .MuiDialogTitle-root': {
          padding: 0,
        },
        '& .MuiDialogContent-root': {
          padding: '0 16px',
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
        ...sx,
      }}
    >
      {(title || showCloseButton) && (
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'relative',
            pb: title && subTitle ? 0.5 : 0,
            mb: showCloseButton ? 2 : 0,
          }}
        >
          <Box display={'flex'} flexDirection={'column'}>
            {title && (
              <Typography variant='h1' sx={sxTitle}>
                {title}
              </Typography>
            )}
            {subTitle && (
              <Typography variant='body2' sx={sxSubTitle}>
                {subTitle}
              </Typography>
            )}
          </Box>
          {showCloseButton && (
            <IconButton onClick={onClose} sx={{position: 'absolute', top: 5, right: 5}}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent>{children}</DialogContent>
    </Dialog>
  );
}
